import { useMediaQuery, useTheme } from '@mui/material'
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridDensity,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowSelectionModel,
  GridTreeNodeWithRender,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import useMassUpdateTasks from 'api/aws/tasks/useMassUpdateTasks'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useEntitiesFromSearch } from 'api/search'
import DrawerWrapper from 'components/DataGrid/DrawerWrapper'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import { dynamicColumnWidths } from 'components/DataGrid/operators/columnWidths'
import { loadModel } from 'components/DataGrid/operators/loadModels'
import { useProcessTaskRowUpdate } from 'components/DataGrid/operators/useProcessTaskRowUpdate'
import { taskGridSxProps } from 'components/DataGrid/taskGridSxProps'
import TasksGridFooter from 'components/DataGrid/TasksGridFooter'
import { useTaskGridUpdates } from 'components/DataGrid/useTaskGridUpdates'
import { useTreeExpansionPersistence } from 'components/DataGrid/useTreeExpansionPersistence'
import TaskDrawer from 'components/TaskDrawer/TaskDrawer'
import { useDrawerContext } from 'context/DrawerProvider/DrawerProvider'
import { useTaskDrawerOpen } from 'context/DrawerProvider/useDrawerState'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useBuildTaskTreeRows } from 'context/useBuildTaskTreeRows'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Subtask, Task, TreeRow } from 'types'
import {
  defaultColumnOrder,
  defaultSortModel,
  defaultVisibilityModel,
  defaultWidths,
  filter_model_team_tasks,
} from './grid/defaultModels'
import { getTeamTaskClassNames } from './grid/getTeamTaskClassNames'
import renderTeamTasksColumns from './grid/renderTeamTasksColumns'

export const replaceIdsInPath = (pathname: string, nextPK: string, nextSK?: string) => {
  // swap ORG_... if present
  let next = /\/ORG_[^/]+/.test(pathname) ? pathname.replace(/\/ORG_[^/]+/, `/${nextPK}`) : pathname

  // swap DEAL_... if present (NCO routes), otherwise leave as-is (MTB has no SK)
  if (nextSK && /\/TASK_[^/]+/.test(next)) {
    next = next.replace(/\/TASK_[^/]+/, `/${nextSK}`)
  }
  return next
}

const TeamTasksPage = () => {
  const { PK, SK } = useParams()
  const theme = useTheme()
  const filters = {}
  const apiRef = useGridApiRef()

  const { openDrawer, closeDrawer } = useDrawerContext()
  const isDrawerOpen = useTaskDrawerOpen()
  const navigate = useNavigate()
  const location = useLocation()
  const { pinned } = useLayoutContext()
  const { buildTaskTreeRows } = useBuildTaskTreeRows()
  const { isExpanded, toggleExpanded } = useTreeExpansionPersistence('teamTasks')
  const { handleMassUpdate, isUpdating } = useMassUpdateTasks('teamTasks')
  const [isMassDeleteOpen, setIsMassDeleteOpen] = useState(false)
  const [footerPosition, setFooterPosition] = useState<{ top: number; left: number } | null>(null)
  const footerRef = useRef<HTMLDivElement | null>(null)

  const {
    organizations,
    deals,
    allTasks,
    commentsTable: comments,
    isItemsFetching,
    isItemsLoading,
  } = useEntitiesFromSearch()
  const [selectedTask, setSelectedTask] = useState<Task>(null)
  const [rowTasks, setRowTasks] = useState<Task[]>([])
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [selectedMassData, setSelectedMassData] = useState<Task[]>([])
  const [lastSelectedRowId, setLastSelectedRowId] = useState<string | null>(null)
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const [customView, setCustomView] = useState<string>(() => {
    return loadModel<string>('teamTasks', 'CustomView', 'default')
  })
  const prevSelectionRef = useRef<Set<string>>(new Set())

  const { processTaskRowUpdate, handleHighPrioChange, handlePostdatedChange } = useProcessTaskRowUpdate()
  const processRowUpdate = (newRow: Task, oldRow: Task, rowSelectionModel: GridRowSelectionModel) => {
    return processTaskRowUpdate(newRow, oldRow, rowSelectionModel)
  }
  const isXL = useMediaQuery(theme.breakpoints.up('xl'))
  const isLG = useMediaQuery(theme.breakpoints.up('lg'))
  const isMD = useMediaQuery(theme.breakpoints.up('md'))
  const isSM = useMediaQuery(theme.breakpoints.up('sm'))

  const screenSize = isXL ? 'xl' : isLG ? 'lg' : isMD ? 'md' : isSM ? 'sm' : 'xs'
  const {
    handleSendForReview,
    handleDeleteClick,
    handleClickMassDelete,
    handleConfirmDelete,
    handleMassDelete,
    handleMassUpdateChange,
    massUpdateData,
    setMassUpdateData,
  } = useTaskGridUpdates({
    tasks: rowTasks,
    setTasks: setRowTasks,
    setIsMassDeleteOpen,
    setAnchorEl,
    apiRef,
  })

  const [density, setDensity] = useState<GridDensity>(() => {
    const saved = localStorage.getItem('teamTasksDensityModel') as GridDensity | null
    return saved ?? 'standard'
  })
  // const digital_tasks = useMemo(() => {
  //   if (!allTasks?.length) return []
  //   const tasks = allTasks.filter(
  //     t => !t?.SK?.startsWith('MONTHLY_') && !t?.isCreative && !t?.SK?.startsWith('PROGRESS_') && t?.type !== 'website'
  //   )
  //   return tasks?.map(t => ({ ...t, rowId: `${t.PK}::${t.SK}` }))
  // }, [allTasks])
  const digital_tasks = useMemo(() => {
    if (!allTasks?.length) return []

    const tasks = allTasks.filter(
      t => !t?.SK?.startsWith('MONTHLY_') && !t?.isCreative && !t?.SK?.startsWith('PROGRESS_') && t?.type !== 'website'
    )

    return tasks.map((t: Task) => {
      const assignerId = typeof t?.assigner === 'string' ? t.assigner : t?.assigner?.user_uuid ?? null

      const assigneeIds = Array.isArray(t?.assignees) ? (t.assignees as string[]) : []

      const assigneeUsers = users?.filter(u => assigneeIds.includes(u.user_uuid)) ?? []
      const assigneeNames = assigneeUsers.map(u => u.name).join(', ')

      return {
        ...t,
        rowId: `${t.PK}::${t.SK}`,
        assigneeNames, // ✅ search/filter only
        // keep assignees as string[]:
      }
    })
  }, [allTasks, users])
  const treeRows: TreeRow[] = useMemo(() => {
    return buildTaskTreeRows(digital_tasks, organizations, comments)
  }, [digital_tasks, organizations, comments])
  /////////////////////////////////////////////////////////////////////// Handle Functions //////////////////////////////////////////////////////////////////////
  // const handleClosePopover = () => {
  //   if (selectedTask) {
  //     const selectedRowId = `${selectedTask.PK}::${selectedTask.SK}`
  //     const rowElement = apiRef.current.getRowElement(selectedRowId)

  //     if (rowElement) {
  //       rowElement.classList.remove('highlighted-row')
  //     }
  //   }
  //   setAnchorEl(null)
  //   setSelectedTask(null)
  // }
  const handleTaskClick = (_: any, taskData: Task) => {
    setSelectedTask(taskData)
    // openDrawer(taskData)
    const { pathname } = location
    if (!/\/ORG_/.test(pathname)) {
      navigate(`/teamtasks/${taskData.PK}/${taskData.SK}/main`, { replace: true })
      return
    }
    const nextPath = replaceIdsInPath(pathname, taskData.PK, taskData.SK)
    navigate(nextPath, { replace: true })
  }

  const handleSubtaskClick = (_: any, taskData: Task) => {
    setSelectedTask(taskData)
    // openDrawer(taskData)
    navigate(`/teamtasks/${taskData.PK}/${taskData.parentSK}/subtasks`)
    // handleClosePopover()
  }
  const handleClickOrganization = (PK: string) => {
    if (!PK) return
    navigate(PK ? `/organizations/${PK}/details` : '#')
  }
  const clickEditStart = (params: GridRenderCellParams) => {
    // again, use the Set’s size
    if (apiRef?.current?.state?.rowSelection.ids.size <= 1) {
      params.api.startCellEditMode({
        id: params.id,
        field: params.field,
      })
    }
  }
  const handleTaskRowModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    // Extract PK::SK
    const parsed = Array.from(rowSelectionModel?.ids).map(id => {
      const [PK, SK] = (id as string).split('::')
      return { id, PK, SK, isSubtask: SK.startsWith('SUBTASK_') }
    })
    const selectedParents = new Set(parsed.filter(p => !p.isSubtask).map(p => p.PK))
    const selectedSubtasks = new Set(parsed.filter(p => p.isSubtask).map(p => p.SK))

    // Rule: If a parent task is selected, remove its subtasks
    const filteredSelection = parsed
      .filter(p => {
        if (p.isSubtask && selectedParents.has(p.PK)) {
          return false // skip subtask if parent is also selected
        }
        return true
      })
      .map(p => p.id)
    // setRowSelectionModel({ ids: new Set(filteredSelection), type: 'include' })

    // Preserve your existing logic for setting selectedTask when only one is selected
    if (filteredSelection.length === 1) {
      const selectedRowId = filteredSelection[0] as string
      const [_pk, sk] = selectedRowId.split('::')

      if (sk.startsWith('SUBTASK_')) {
        const parentTask = digital_tasks.find(task => task.subtasks?.some((sub: Subtask) => sub.SK === sk))
        if (parentTask) {
          const selectedSubtask = parentTask.subtasks.find((sub: Subtask) => sub.SK === sk)
          if (selectedSubtask) {
            const subtaskWithParentAssignees: Subtask = {
              ...selectedSubtask,
              parentSK: parentTask.SK,
              assignees: parentTask.assignees ?? [],
            }
            setSelectedTask(subtaskWithParentAssignees)
          }
        }
      } else {
        const selected = digital_tasks.find(task => task.SK === sk)
        if (selected) {
          setSelectedTask(selected)
        }
      }
    } else {
      setSelectedTask(null)
    }
  }
  useEffect(() => {
    if (!footerRef.current) return

    const el = footerRef.current
    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect()
      setFooterPosition({ top: rect.top - 12, left: rect.left + rect.width / 2 })
    })

    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const clearSelections = () => {
    setCustomView('default')
    setMassUpdateData({})
  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  const isCellEditable = (
    params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
    rowSelectionModel: GridRowSelectionModel
  ) => {
    // Check if the column is `invoiceStatus`
    if (params.field === 'masterStatus') {
      // Allow editing only if one or no rows are selected
      return Array.from(rowSelectionModel?.ids).length <= 1
    }
    if (params.field === 'highPrio') {
      return Array.from(rowSelectionModel?.ids).length <= 1
    }
    if (params.field === 'startDate') {
      return Array.from(rowSelectionModel?.ids).length <= 1
    }
    if (params.field === 'endDate') {
      return Array.from(rowSelectionModel?.ids).length <= 1
    }
    return true // Default for other columns
  }

  const columns = useMemo(() => {
    return renderTeamTasksColumns(
      organizations,
      users,
      'teamtasks',
      handleClickOrganization,
      density,
      handleTaskClick,
      clickEditStart,
      handleHighPrioChange,
      customView,
      handlePostdatedChange
    )
  }, [
    organizations,
    users,
    handleClickOrganization,
    theme,
    density,
    handleTaskClick,
    handleSubtaskClick,
    clickEditStart,
    handleHighPrioChange,
    handlePostdatedChange,
    handleSendForReview,
    customView,
    isExpanded,
    toggleExpanded,
  ]) as GridColDef[]
  const changeColumnVisibility = (newModel: GridColumnVisibilityModel) => {
    if (newModel?.minimalist) setCustomView('minimalist')
    else if (newModel?.textOnly) setCustomView('textOnly')
    else if (newModel?.websiteAccess) setCustomView('websiteAccess')
  }
  const closeTaskDrawer = React.useCallback(() => {
    // closeDrawer()
    setSelectedTask(null)
    navigate('/teamtasks')
  }, [closeDrawer, navigate])
  // stable callbacks used by the footer
  const onChange = React.useCallback(handleMassUpdateChange, [handleMassUpdateChange])
  const onUpdate = React.useCallback(handleMassUpdate, [handleMassUpdate])
  const onClickDelete = React.useCallback((model: GridRowSelectionModel) => handleDeleteClick(model), [
    handleDeleteClick,
  ])
  const onClickMassDelete = React.useCallback(
    (model: GridRowSelectionModel) => handleClickMassDelete(model, setSelectedMassData),
    [handleClickMassDelete]
  )
  const onDelete = React.useCallback(() => handleConfirmDelete(selectedTask, setSelectedTask, setIsDeleting), [
    selectedTask,
    handleConfirmDelete,
  ])
  const onMassDelete = React.useCallback(
    (rowSelectionModel, setRowSelectionModel) =>
      handleMassDelete(setSelectedTask, rowSelectionModel, setRowSelectionModel, setSelectedMassData, setIsDeleting),
    [handleMassDelete]
  )

  const onClosePopover = React.useCallback(() => {
    // any extra local cleanup goes here (besides selection, which Wrapper handles)
    setAnchorEl(null)
  }, [])

  const onCloseMassDeletePopover = React.useCallback(() => {
    setIsMassDeleteOpen(false)
    // additional cleanup if needed
  }, [])

  const footerProps = React.useMemo(
    () => ({
      users,
      selectedData: selectedTask,
      selectedMassData,
      setSelectedMassData,
      isLoading: isUsersLoading,
      massUpdateData,
      rowCount: digital_tasks?.length ?? 0,
      onChange,
      onUpdate,
      onClickDelete,
      onClickMassDelete,
      onDelete,
      // Wrapper will pass (rowSelectionModel, setRowSelectionModel) here for you:
      onMassDelete,
      isDeleting,
      onClosePopover,
      onCloseMassDeletePopover,
      onClear: clearSelections,
      tasks: digital_tasks,
      anchorEl,
      context: 'teamTasks',
      customPosition: footerPosition,
      isMassDeleteOpen,
    }),
    [
      users,
      selectedTask,
      selectedMassData,
      isUsersLoading,
      massUpdateData,
      digital_tasks,
      onChange,
      onUpdate,
      onClickDelete,
      onClickMassDelete,
      onDelete,
      onMassDelete,
      isDeleting,
      onClosePopover,
      onCloseMassDeletePopover,
      clearSelections,
      anchorEl,
      footerPosition,
      isMassDeleteOpen,
    ]
  )
  const allRowIds = useMemo(() => {
    if (apiRef.current) return apiRef.current.getAllRowIds()
  }, [apiRef.current])
  const getRowClassNameWrapper = (params: GridRowClassNameParams, rowSelectionModel: GridRowSelectionModel) =>
    getTeamTaskClassNames(params, rowSelectionModel, allRowIds)
  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  useEffect(() => {
    if (!PK || !SK) {
      if (isDrawerOpen) closeDrawer()
      return
    }

    const selected = treeRows?.find(t =>
      t?.SK?.startsWith('SUBTASK_') ? t?.PK === PK && t?.GSI1PK === SK : t?.SK === SK
    )
    if (!selected) return

    // Only update selectedTask if it actually changed
    setSelectedTask(prev => (prev?.PK === selected.PK && prev?.SK === selected.SK ? prev : selected))

    // Only open once
    if (!isDrawerOpen) openDrawer()
  }, [PK, SK, treeRows, isDrawerOpen])
  // Close on unmount only
  useEffect(() => {
    return () => {
      closeDrawer()
      setSelectedTask(null)
    }
  }, [])
  const changeDensity = (newDensity: GridDensity) => {
    setDensity(newDensity)
  }
  const defaultColumnWidths = useMemo(() => {
    return dynamicColumnWidths(screenSize, defaultWidths, columns)
  }, [columns, screenSize])
  const selectedRowId = useMemo(() => {
    if (!selectedTask) return null
    return `${selectedTask?.PK}::${selectedTask?.SK}`
  }, [selectedTask])
  useEffect(() => {
    if (selectedRowId) setLastSelectedRowId(selectedRowId)
    // else if (!selectedRowId && !isDrawerOpen) setLastSelectedRowId(null)
  }, [selectedRowId])

  const updateLastSelectedFromSelection = (model: GridRowSelectionModel) => {
    const next = new Set(Array.from(model.ids).map(String))
    const prev = prevSelectionRef.current

    const added: string[] = []
    const removed: string[] = []

    for (const id of next) if (!prev.has(id)) added.push(id)
    for (const id of prev) if (!next.has(id)) removed.push(id)

    // If user just checked one row -> persist highlight to that row
    if (added.length === 1) {
      setLastSelectedRowId(added[0])
    }

    // If user just unchecked one row and now selection is empty -> persist highlight to that row
    if (removed.length === 1 && next.size === 0) {
      setLastSelectedRowId(removed[0])
    }

    prevSelectionRef.current = next
  }
  return (
    <>
      <GlobalDataGrid
        columns={columns}
        rows={treeRows}
        apiRef={apiRef}
        context={'teamtasks'}
        isExpanded={isExpanded}
        defaultFilterModel={filter_model_team_tasks}
        defaultVisibilityModel={defaultVisibilityModel}
        defaultSortModel={defaultSortModel}
        defaultGridDensity={'standard'}
        defaultColumnWidths={defaultColumnWidths}
        defaultColumnOrder={defaultColumnOrder}
        pinnedColumns={{ left: [], right: [] }}
        onRowSelectionModelChange={(rowSelectionModel: GridRowSelectionModel) => {
          updateLastSelectedFromSelection(rowSelectionModel)
          handleTaskRowModelChange(rowSelectionModel)
        }}
        onProcessRowUpdate={processTaskRowUpdate}
        isCellEditable={isCellEditable}
        onChangeColumnVisibility={changeColumnVisibility}
        customView={customView}
        setCustomView={setCustomView}
        toolbarComponent={GlobalGridToolbar}
        FooterComponent={TasksGridFooter} // ← tell grid which selection footer to render
        footerProps={footerProps}
        DrawerComponent={DrawerWrapper}
        drawerProps={{
          closeDrawer: closeTaskDrawer,
          context: 'teamtasks',
          selectedRowId,
          Component: TaskDrawer,
        }}
        isDrawerOpen={isDrawerOpen}
        onChangeDensity={changeDensity}
        treeData={true}
        getRowClassName={getRowClassNameWrapper}
        gridSxProps={taskGridSxProps}
        isLoading={isDeleting}
        selectedRowId={selectedRowId}
        lastSelectedRowId={lastSelectedRowId} // persistent highlight row
        onClearLastSelectedRow={() => setLastSelectedRowId(null)}
        // gridContainerSx={{
        //   position: 'relative',
        //   pt: pinned ? '60px' : 0.8,
        //   transition: 'all 0.3s ease', // or .5s, etc.
        //   width: '100%',
        //   height: '100%',
        //   overflowX: 'auto',
        //   overflowY: 'hidden',
        //   zIndex: 1150,
        // }}
      />
    </>
  )
}
export default TeamTasksPage
